import { BranchType } from "@/features/church/config/branches/types/branch.types"
import { FinanceCategoryScope } from "@/features/finance/model/finance-types"

/** Loose on purpose: the store's `Branch` and auth's `BranchSummary` both flow through here. */
type BranchLike = { branchType?: BranchType | string | null }

/**
 * What the workspace-level URL (`/:ws`, carried as the `parish` token) resolves to on a finance page.
 *
 * - MULTI-branch: the parish/SYSTEM branch — the organization console, a real place with its own books.
 * - MONO: the operational branch, and NEVER the parish. A mono church's parish is invisible: nobody holds
 *   access to it, and it holds nothing. Resolving it there produced empty lists and denials across finance,
 *   budgets, reports and pickers, because a parish-scoped read authorized against a branch no member can reach.
 *
 * The mono case filters rather than trusting the list: `buildAccessibleBranches` already omits a mono parish,
 * but the finance page also falls back to the branches STORE, and a defensive rule costs nothing next to a
 * failure mode this quiet.
 */
export function resolveWorkspaceContextBranch<T extends BranchLike>(
  candidates: readonly T[],
  options: { isMonoBranchWorkspace: boolean },
): T | null {
  if (options.isMonoBranchWorkspace) {
    return candidates.find((branch) => branch.branchType !== BranchType.System) ?? null
  }

  return candidates.find((branch) => branch.branchType === BranchType.System) ?? null
}

/**
 * What a finance page's data is ABOUT, derived from the branch it resolved.
 *
 * Eighty call sites read this. They are correct only while a mono workspace can never reach parish context —
 * see `resolveWorkspaceContextBranch` — which is why that is asserted rather than assumed.
 */
export function resolveFinancePageContext(branch: BranchLike | null | undefined): "branch" | "parish" {
  return branch?.branchType === BranchType.System ? "parish" : "branch"
}

/** Convert semantic page context into explicit API scope; a branch id must never be used to infer it. */
export function resolveFinanceCategoryScope(context: "branch" | "parish"): FinanceCategoryScope {
  return context === "parish" ? FinanceCategoryScope.Parish : FinanceCategoryScope.Branch
}
